import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from '../../../lib/useAuthToken';
import type { InventoryItem, StockMovement, Paginated, StockResult, StockSummaryItem, CostHistoryItem, InventoryCategory } from '../api/inventory.client';
import { createItem, createMovement, getItem, getStock, getStockSummary, listItems, listMovements, updateItem, deleteItem, getCostHistory, adjustStock, listCategories, createCategory, updateCategory, deleteCategory } from '../api/inventory.client';
import { listRecipes, getRecipe, upsertRecipe, type UpsertRecipePayload } from '../api/recipe.client';

const defaults = { staleTime: 60_000, retry: 1 } as const;
const enabled = (token: string | null, extra?: boolean) => !!token && extra !== false;

export const useItems = (params?: Record<string, unknown>) => { const t = useAuthToken(); return useQuery<Paginated<InventoryItem>>({ queryKey: ['inventory','items',params], queryFn: () => listItems(params), enabled: enabled(t), ...defaults }); };
export const useItem = (id?: string) => { const t = useAuthToken(); return useQuery<InventoryItem>({ queryKey: ['inventory','item',id], queryFn: () => getItem(id as string), enabled: enabled(t, !!id), ...defaults }); };
export const useMovements = (params?: Record<string, unknown>) => { const t = useAuthToken(); return useQuery<Paginated<StockMovement>>({ queryKey: ['inventory','movements',params], queryFn: () => listMovements(params), enabled: enabled(t), ...defaults }); };
export const useStock = (params?: Record<string, unknown>) => { const t = useAuthToken(); return useQuery<StockResult[]>({ queryKey: ['inventory','stock',params], queryFn: () => getStock(params), enabled: enabled(t), ...defaults }); };
export const useStockSummary = (params?: Record<string, unknown>) => { const t = useAuthToken(); return useQuery<StockSummaryItem[]>({ queryKey: ['inventory','stockSummary',params], queryFn: () => getStockSummary(params), enabled: enabled(t), ...defaults }); };

export const useCreateItem = () => { const qc = useQueryClient(); return useMutation({ mutationFn: createItem, onSuccess: () => { void qc.invalidateQueries({ queryKey: ['inventory','items'] }); } }); };
export const useUpdateItem = () => { const qc = useQueryClient(); return useMutation({ mutationFn: ({ id, data }: { id: string; data: Partial<InventoryItem> }) => updateItem(id, data), onSuccess: (_, { id }) => { void qc.invalidateQueries({ queryKey: ['inventory','items'] }); void qc.invalidateQueries({ queryKey: ['inventory','item',id] }); void qc.invalidateQueries({ queryKey: ['inventory','stock'] }); void qc.invalidateQueries({ queryKey: ['inventory','stockSummary'] }); } }); };
export const useDeleteItem = () => { const qc = useQueryClient(); return useMutation({ mutationFn: deleteItem, onSuccess: () => { void qc.invalidateQueries({ queryKey: ['inventory','items'] }); } }); };
export const useCreateMovement = () => { const qc = useQueryClient(); return useMutation({ mutationFn: createMovement, onSuccess: () => { void qc.invalidateQueries({ queryKey: ['inventory','movements'] }); void qc.invalidateQueries({ queryKey: ['inventory','stock'] }); void qc.invalidateQueries({ queryKey: ['inventory','stockSummary'] }); void qc.invalidateQueries({ queryKey: ['inventory','items'] }); } }); };

export const useCostHistory = (itemId?: string | number) => {
  const t = useAuthToken();
  return useQuery<CostHistoryItem[]>({
    queryKey: ['inventory', 'cost-history', itemId],
    queryFn: () => getCostHistory(itemId!),
    enabled: enabled(t, !!itemId),
    ...defaults,
  });
};

export const useAdjustStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: { new_stock: number; reason?: string; type?: string } }) =>
      adjustStock(id, data),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'stockSummary'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'movements'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'item', String(id)] });
    },
  });
};

// ── Categorías de Inventario ──────────────────────────────────────────────────
export const useInventoryCategories = () => {
  const t = useAuthToken();
  return useQuery<InventoryCategory[]>({
    queryKey: ['inventory', 'categories'],
    queryFn: listCategories,
    enabled: enabled(t),
    ...defaults,
  });
};

export const useCreateInventoryCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) => createCategory(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'categories'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });
};

export const useUpdateInventoryCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: { name: string } }) => updateCategory(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'categories'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });
};

export const useDeleteInventoryCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteCategory(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'categories'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });
};

// ── Recetas ────────────────────────────────────────────────────────────────────
export const useRecipes = (q?: string) => { const t = useAuthToken(); return useQuery({ queryKey: ['inventory','recipes', q], queryFn: () => listRecipes(q), enabled: enabled(t), ...defaults }); };
export const useRecipe = (productId?: string) => { const t = useAuthToken(); return useQuery({ queryKey: ['inventory','recipe',productId], queryFn: () => getRecipe(productId!), enabled: enabled(t, !!productId), ...defaults }); };
export const useUpsertRecipe = () => { const qc = useQueryClient(); return useMutation({ mutationFn: (payload: UpsertRecipePayload) => upsertRecipe(payload), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['inventory','recipes'] }); } }); };

// ── Auditoría Masiva de Stock (Toma Física de Inventario) ────────────────────
export const useAuditStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: import('../api/inventory.client').StockAuditPayload) =>
      import('../api/inventory.client').then(m => m.auditStock(payload)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'stockSummary'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'movements'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'audits'] });
    },
  });
};

export const useAudits = () => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['inventory', 'audits'],
    queryFn: () => import('../api/inventory.client').then(m => m.listAudits()),
    enabled: enabled(t),
    ...defaults,
  });
};

export const useAuditDetail = (id?: number | string) => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['inventory', 'audit', id],
    queryFn: () => import('../api/inventory.client').then(m => m.getAudit(id!)),
    enabled: enabled(t, !!id),
    ...defaults,
  });
};

// ── Kardex y Trazabilidad de Insumo ──────────────────────────────────────────
export const useItemKardex = (itemId?: number | string, params?: Record<string, unknown>) => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['inventory', 'kardex', itemId, params],
    queryFn: () => import('../api/inventory.client').then(m => m.getItemKardex(itemId!, params)),
    enabled: enabled(t, !!itemId),
    ...defaults,
  });
};

// ── Sugerencia Automática de Compras ──────────────────────────────────────────
export const useReplenishmentSuggestions = () => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['inventory', 'replenishment-suggestions'],
    queryFn: () => import('../api/inventory.client').then(m => m.getReplenishmentSuggestions()),
    enabled: enabled(t),
    ...defaults,
  });
};

export const useGenerateSuggestedOrders = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { orders: any[] }) =>
      import('../api/inventory.client').then(m => m.generateSuggestedOrders(payload)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'replenishment-suggestions'] });
      void qc.invalidateQueries({ queryKey: ['purchases', 'orders'] });
    },
  });
};

// ── Sub-recetas y Producción Interna (Mise en Place) ─────────────────────────
export const useSubRecipes = () => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['inventory', 'sub-recipes'],
    queryFn: () => import('../api/inventory.client').then(m => m.listSubRecipes()),
    enabled: enabled(t),
    ...defaults,
  });
};

export const useCreateSubRecipe = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => import('../api/inventory.client').then(m => m.createSubRecipe(data)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'sub-recipes'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });
};

export const useUpdateSubRecipe = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: any }) =>
      import('../api/inventory.client').then(m => m.updateSubRecipe(id, data)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'sub-recipes'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });
};

export const useDeleteSubRecipe = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) =>
      import('../api/inventory.client').then(m => m.deleteSubRecipe(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'sub-recipes'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });
};

export const useProduceSubRecipeBatch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: { batch_qty: number; notes?: string } }) =>
      import('../api/inventory.client').then(m => m.produceSubRecipeBatch(id, data)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'sub-recipes'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'movements'] });
    },
  });
};

// ── Control Rápido de Mermas y Desperdicios (Waste Log) ───────────────────────
export const useWastes = (params?: Record<string, unknown>) => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['inventory', 'wastes', params],
    queryFn: () => import('../api/inventory.client').then(m => m.listWastes(params)),
    enabled: enabled(t),
    ...defaults,
  });
};

export const useCreateWaste = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof import('../api/inventory.client')['createWaste']>[0]) =>
      import('../api/inventory.client').then(m => m.createWaste(data)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'wastes'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'movements'] });
    },
  });
};



